import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import apiRequest from '../utils/api';

interface User {
  id: number;
  username: string;
  fullName: string;
  email: string;
  role: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface UserFormData {
  username: string;
  fullName: string;
  email: string;
  password?: string;
  role: string;
  status: string;
}

interface Permission {
  module: string;
  permission: string;
  granted: boolean;
}

interface AvailablePermissions {
  [module: string]: string[];
}

const Users: React.FC = () => {
  const { hasAnyPermission, hasPermission } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<UserFormData>({
    username: '',
    fullName: '',
    email: '',
    password: '',
    role: 'viewer',
    status: 'active',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changePassword, setChangePassword] = useState(false);
  const [showPermissions, setShowPermissions] = useState(false);
  const [availablePermissions, setAvailablePermissions] = useState<AvailablePermissions>({});
  const [userPermissions, setUserPermissions] = useState<Permission[]>([]);
  const [rolePermissions, setRolePermissions] = useState<Permission[]>([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);

  const roles = [
    { value: 'admin', label: 'Admin' },
    { value: 'manager', label: 'Manager' },
    { value: 'supervisor', label: 'Supervisor' },
    { value: 'staff', label: 'Staff' },
    { value: 'viewer', label: 'Viewer' },
  ];

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const offset = (currentPage - 1) * itemsPerPage;
      const response = await apiRequest(`/api/users?limit=${itemsPerPage}&offset=${offset}`);
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.data || []);
        setTotalItems(data.pagination?.total || 0);
      } else {
        const errorData = await response.json();
        console.error('Users API Error:', response.status, errorData);
        throw new Error(errorData.error || 'Không thể tải danh sách người dùng');
      }
    } catch (error: any) {
      setError(error.message || 'Có lỗi xảy ra khi tải dữ liệu');
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    fetchUsers();
    fetchAvailablePermissions();
  }, [fetchUsers]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const errors: Record<string, string> = {};
    if (!formData.username.trim()) errors.username = 'Tên đăng nhập không được để trống';
    if (!formData.fullName.trim()) errors.fullName = 'Họ tên không được để trống';
    if (!formData.email.trim()) errors.email = 'Email không được để trống';
    if (!editingUser) {
      if (!formData.password) errors.password = 'Mật khẩu không được để trống';
      if (formData.password !== confirmPassword) errors.password = 'Mật khẩu xác nhận không khớp';
    } else if (changePassword) {
      if (!formData.password) errors.password = 'Nhập mật khẩu mới';
      if (formData.password !== confirmPassword) errors.password = 'Mật khẩu xác nhận không khớp';
    }
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      const url = editingUser 
        ? `/api/users/${editingUser.id}`
        : '/api/users';
      
      const body: any = {
        username: formData.username,
        fullName: formData.fullName,
        email: formData.email,
        role: formData.role,
        status: formData.status,
      };
      if (!editingUser || (editingUser && changePassword)) {
        if (formData.password) body.password = formData.password;
      }

      
      const response = await apiRequest(url, {
        method: editingUser ? 'PUT' : 'POST',
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const userData = await response.json();
        const userId = userData.data.id;

        // Save permissions for new user if permissions are set
        if (!editingUser && showPermissions && userPermissions.length > 0) {
          await apiRequest(`/api/users/${userId}/permissions/bulk`, {
            method: 'PUT',
            body: JSON.stringify({ permissions: userPermissions })
          });
        }

        await fetchUsers();
        resetForm();
        setShowModal(false);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Có lỗi xảy ra');
      }
    } catch (error: any) {
      setError(error.message || 'Có lỗi xảy ra');
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      fullName: user.fullName,
      email: user.email,
      password: '',
      role: user.role,
      status: user.status,
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa người dùng này?')) return;

    try {
      const response = await apiRequest(`/api/users/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await fetchUsers();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Không thể xóa người dùng');
      }
    } catch (error: any) {
      setError(error.message || 'Có lỗi xảy ra');
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      fullName: '',
      email: '',
      password: '',
      role: 'viewer',
      status: 'active',
    });
    setFormErrors({});
    setEditingUser(null);
    setShowPermissions(false);
    setUserPermissions([]);
    setRolePermissions([]);
  };

  const fetchAvailablePermissions = async () => {
    try {
      const response = await apiRequest('/api/users/permissions/available');
      
      if (response.ok) {
        const data = await response.json();
        setAvailablePermissions(data.data);
      }
    } catch (error) {
      console.error('Error fetching available permissions:', error);
    }
  };

  const fetchUserPermissions = async (userId: number) => {
    try {
      const response = await apiRequest(`/api/users/${userId}/permissions`);
      
      if (response.ok) {
        const data = await response.json();
        setRolePermissions(data.data.rolePermissions.map((p: any) => ({
          module: p.module,
          permission: p.permission,
          granted: true
        })));
        setUserPermissions(data.data.userPermissions);
      }
    } catch (error) {
      console.error('Error fetching user permissions:', error);
    }
  };

  const savePermissions = async () => {
    if (!editingUser) return;

    try {
      
      const response = await apiRequest(`/api/users/${editingUser.id}/permissions/bulk`, {
        method: 'PUT',
        body: JSON.stringify({ permissions: userPermissions })
      });

      if (response.ok) {
        setError('');
        alert('Permissions updated successfully!');
        setShowPermissions(false);
        // Refresh permissions to see actual saved state
        fetchUserPermissions(editingUser.id);
        
        // Refresh AuthContext if editing current user
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (currentUser.id === editingUser.id) {
          window.location.reload(); // Force refresh to update permissions in AuthContext
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Error updating permissions');
      }
    } catch (error: any) {
      setError(error.message || 'Error updating permissions');
    }
  };

  const togglePermission = (module: string, permission: string) => {
    setUserPermissions(prev => {
      const existing = prev.find(p => p.module === module && p.permission === permission);
      const isRolePermission = rolePermissions.some(p => 
        p.module === module && p.permission === permission
      );
      
      if (existing) {
        return prev.map(p => 
          p.module === module && p.permission === permission 
            ? { ...p, granted: !p.granted }
            : p
        );
      } else {
        // If it's a role permission being unchecked, add it as granted: false to override
        const granted = !isRolePermission;
        return [...prev, { module, permission, granted }];
      }
    });
  };

  const selectAllPermissions = () => {
    const allPermissions: Permission[] = [];
    Object.entries(availablePermissions).forEach(([module, permissions]) => {
      permissions.forEach(permission => {
        allPermissions.push({ module, permission, granted: true });
      });
    });
    setUserPermissions(allPermissions);
  };

  const clearAllPermissions = () => {
    if (editingUser) {
      // Reset to actual permissions from server for existing user
      fetchUserPermissions(editingUser.id);
    } else {
      // For new user, just clear local selections
      setUserPermissions([]);
    }
  };

  // For now, disable client-side filtering to work with server-side pagination
  // TODO: Implement server-side search/filter
  const filteredUsers = users;

  const formatDate = (dateString: string) => {
    const dt = new Date(dateString);
    const pad = (n: number) => String(n).padStart(2, '0');
    const d = pad(dt.getUTCDate());
    const m = pad(dt.getUTCMonth() + 1);
    const y = dt.getUTCFullYear();
    const hh = pad(dt.getUTCHours());
    const mm = pad(dt.getUTCMinutes());
    const ss = pad(dt.getUTCSeconds());
    return `${d}/${m}/${y} ${hh}:${mm}:${ss}`;
  };

  const getRoleBadge = (role: string) => {
    const roleStyles = {
      admin: { backgroundColor: '#dc2626', color: 'white' },
      manager: { backgroundColor: '#ea580c', color: 'white' },
      supervisor: { backgroundColor: '#2563eb', color: 'white' },
      staff: { backgroundColor: '#16a34a', color: 'white' },
      viewer: { backgroundColor: '#6b7280', color: 'white' },
    };

    const style = roleStyles[role as keyof typeof roleStyles] || roleStyles.viewer;
    const roleLabels = {
      admin: 'Admin',
      manager: 'Manager',
      supervisor: 'Supervisor',
      staff: 'Staff',
      viewer: 'Viewer',
    };

    return (
      <span style={{
        ...style,
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '500'
      }}>
        {roleLabels[role as keyof typeof roleLabels] || role}
      </span>
    );
  };

  // Check if user has permission for users module
  if (!hasAnyPermission('users')) {
    return (
      <Layout currentPage="users">
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '400px',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <div style={{ fontSize: '48px' }}>🔒</div>
          <div style={{ 
            fontSize: '18px', 
            fontWeight: '600', 
            color: '#374151',
            textAlign: 'center'
          }}>
            Không có quyền truy cập
          </div>
          <div style={{ 
            fontSize: '14px', 
            color: '#6b7280',
            textAlign: 'center'
          }}>
            Bạn không có quyền truy cập module này. Vui lòng liên hệ quản trị viên.
          </div>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout currentPage="users">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px' }}>
          <div className="loading-spinner" style={{ width: '40px', height: '40px' }}></div>
        </div>
      </Layout>
    );
  }


  return (
    <Layout currentPage="users">
      <div style={{ 
        width: '100%',
        maxWidth: '100%'
      }}>
        {error && (
          <div style={{
            backgroundColor: '#fecaca',
            border: '1px solid #f87171',
            color: '#dc2626',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '24px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          gap: '16px',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', gap: '12px', flex: 1, minWidth: '300px' }}>
            <input
              type="text"
              placeholder="Tìm kiếm theo tên đăng nhập, họ tên, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                flex: 1,
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                minWidth: '120px'
              }}
            >
              <option value="">Tất cả vai trò</option>
              {roles.map(role => (
                <option key={role.value} value={role.value}>{role.label}</option>
              ))}
            </select>
          </div>
          
          {hasPermission('users', 'create') && (
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              ➕ Thêm người dùng
            </button>
          )}
        </div>

        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ 
            overflowX: 'auto', 
            WebkitOverflowScrolling: 'touch'
          }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              minWidth: window.innerWidth <= 768 ? '700px' : '100%'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>ID</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Tên đăng nhập</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Họ tên</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Email</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Vai trò</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Trạng thái</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Ngày tạo</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user, index) => (
                  <tr key={user.id} style={{
                    borderBottom: index === filteredUsers.length - 1 ? 'none' : '1px solid #f3f4f6'
                  }}>
                    <td style={{ padding: '12px', fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                      {user.id}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#374151' }}>
                      {user.username}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#374151' }}>
                      {user.fullName}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#374151' }}>
                      {user.email}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {getRoleBadge(user.role)}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        backgroundColor: user.status === 'active' ? '#dcfce7' : user.status === 'inactive' ? '#fee2e2' : '#fef3c7',
                        color: user.status === 'active' ? '#16a34a' : user.status === 'inactive' ? '#dc2626' : '#d97706'
                      }}>
                        {user.status === 'active' ? 'Hoạt động' : user.status === 'inactive' ? 'Tạm khóa' : 'Tạm dừng'}
                      </span>
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px', color: '#6b7280' }}>
                      {formatDate(user.createdAt)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                        {hasPermission('users', 'update') && (
                          <button
                            onClick={() => handleEdit(user)}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#f3f4f6',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              fontSize: '12px',
                              cursor: 'pointer'
                            }}
                          >
                            ✏️
                          </button>
                        )}
                        {hasPermission('users', 'delete') && (
                          <button
                            onClick={() => handleDelete(user.id)}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#fee2e2',
                              border: '1px solid #fca5a5',
                              borderRadius: '4px',
                              fontSize: '12px',
                              cursor: 'pointer'
                            }}
                          >
                            🗑️
                          </button>
                        )}
                        {!hasPermission('users', 'update') && !hasPermission('users', 'delete') && (
                          <span style={{
                            padding: '4px 8px',
                            color: '#9ca3af',
                            fontSize: '12px'
                          }}>
                            Không có quyền
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ 
                      padding: '40px', 
                      textAlign: 'center', 
                      color: '#9ca3af',
                      fontSize: '14px'
                    }}>
                      {searchTerm || roleFilter ? 'Không tìm thấy người dùng nào phù hợp' : 'Chưa có người dùng nào'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && totalItems > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: '20px',
              padding: '16px',
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              flexWrap: 'wrap',
              gap: '16px'
            }}>
              {/* Items per page selector */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '14px', color: '#6b7280' }}>Hiển thị:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: '4px 8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: 'white'
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span style={{ fontSize: '14px', color: '#6b7280' }}>dòng</span>
              </div>

              {/* Pagination info */}
              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                Hiển thị {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} trong tổng {totalItems} người dùng
              </div>

              {/* Pagination buttons */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: currentPage === 1 ? '#f9fafb' : 'white',
                    color: currentPage === 1 ? '#9ca3af' : '#374151',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Đầu
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: currentPage === 1 ? '#f9fafb' : 'white',
                    color: currentPage === 1 ? '#9ca3af' : '#374151',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Trước
                </button>
                
                {/* Page numbers */}
                {(() => {
                  const totalPages = Math.ceil(totalItems / itemsPerPage);
                  const pages = [];
                  const maxVisiblePages = 5;
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                  
                  if (endPage - startPage < maxVisiblePages - 1) {
                    startPage = Math.max(1, endPage - maxVisiblePages + 1);
                  }
                  
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        style={{
                          padding: '6px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          backgroundColor: currentPage === i ? '#3b82f6' : 'white',
                          color: currentPage === i ? 'white' : '#374151',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: currentPage === i ? '600' : '400'
                        }}
                      >
                        {i}
                      </button>
                    );
                  }
                  return pages;
                })()}

                <button
                  onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalItems / itemsPerPage), prev + 1))}
                  disabled={currentPage >= Math.ceil(totalItems / itemsPerPage)}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: currentPage >= Math.ceil(totalItems / itemsPerPage) ? '#f9fafb' : 'white',
                    color: currentPage >= Math.ceil(totalItems / itemsPerPage) ? '#9ca3af' : '#374151',
                    cursor: currentPage >= Math.ceil(totalItems / itemsPerPage) ? 'not-allowed' : 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Sau
                </button>
                <button
                  onClick={() => setCurrentPage(Math.ceil(totalItems / itemsPerPage))}
                  disabled={currentPage >= Math.ceil(totalItems / itemsPerPage)}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: currentPage >= Math.ceil(totalItems / itemsPerPage) ? '#f9fafb' : 'white',
                    color: currentPage >= Math.ceil(totalItems / itemsPerPage) ? '#9ca3af' : '#374151',
                    cursor: currentPage >= Math.ceil(totalItems / itemsPerPage) ? 'not-allowed' : 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Cuối
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              width: '90%',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600' }}>
                {editingUser ? 'Chỉnh sửa người dùng' : 'Thêm người dùng mới'}
              </h3>

              <form onSubmit={handleSubmit}>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                    Tên đăng nhập *
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: formErrors.username ? '2px solid #ef4444' : '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                  {formErrors.username && (
                    <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                      {formErrors.username}
                    </div>
                  )}
                </div>

                

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                    Họ tên *
                  </label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: formErrors.fullName ? '2px solid #ef4444' : '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                  {formErrors.fullName && (
                    <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                      {formErrors.fullName}
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: formErrors.email ? '2px solid #ef4444' : '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                  {formErrors.email && (
                    <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                      {formErrors.email}
                    </div>
                  )}
                </div>

                
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                    Vai trò
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  >
                    {roles.map(role => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                    Trạng thái
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  >
                    <option value="active">Hoạt động</option>
                    <option value="inactive">Tạm khóa</option>
                    <option value="suspended">Tạm dừng</option>
                  </select>
                </div>

                {hasPermission('users', 'manage_permissions') && (
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      <input
                        type="checkbox"
                        checked={showPermissions}
                        onChange={(e) => {
                          setShowPermissions(e.target.checked);
                          if (e.target.checked) {
                            if (editingUser) {
                              fetchUserPermissions(editingUser.id);
                            } else {
                              setUserPermissions([]);
                              setRolePermissions([]);
                            }
                          }
                        }}
                        style={{ margin: 0 }}
                      />
                      🔑 Phân quyền chi tiết
                    </label>

                    {showPermissions && (
                      <div style={{
                        marginTop: '16px',
                        padding: '16px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        backgroundColor: '#f9fafb'
                      }}>
                        {editingUser?.role === 'admin' && (
                          <div style={{
                            marginBottom: '12px',
                            padding: '8px 12px',
                            backgroundColor: '#e0f2fe',
                            border: '1px solid #93c5fd',
                            borderRadius: '6px',
                            color: '#1d4ed8',
                            fontSize: '13px'
                          }}>
                            Tài khoản Admin có đầy đủ quyền. Không cần cấp thủ công.
                          </div>
                        )}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '16px'
                        }}>
                          <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                            Quản lý quyền
                          </h4>
                          {editingUser?.role !== 'admin' && (
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                type="button"
                                onClick={selectAllPermissions}
                                style={{
                                  padding: '4px 8px',
                                  backgroundColor: '#16a34a',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  cursor: 'pointer'
                                }}
                              >
                                Chọn tất cả
                              </button>
                              <button
                                type="button"
                                onClick={clearAllPermissions}
                                style={{
                                  padding: '4px 8px',
                                  backgroundColor: '#dc2626',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  cursor: 'pointer'
                                }}
                              >
                                Bỏ chọn tất cả
                              </button>
                            </div>
                          )}
                        </div>

                        <div style={{ 
                          display: 'grid', 
                          gap: '12px',
                          fontSize: '14px'
                        }}>
                          {Object.entries(availablePermissions).map(([module, permissions]) => (
                            <div key={module} style={{
                              padding: '12px',
                              backgroundColor: 'white',
                              borderRadius: '6px',
                              border: '1px solid #d1d5db'
                            }}>
                              <h5 style={{ 
                                margin: '0 0 8px 0', 
                                fontSize: '14px', 
                                fontWeight: '600',
                                color: '#374151',
                                textTransform: 'capitalize'
                              }}>
                                {module === 'users' ? '👤 Người dùng' :
                                 module === 'orders' ? '📦 Đơn hàng' :
                                 module === 'orders_columns' ? '👁️ Hiển thị cột (Đơn hàng)' :
                                 module === 'reports' ? '📊 Báo cáo' :
                                 module === 'dashboard' ? '🏠 Trang chủ' :
                                 module === 'whatsapp' ? '💬 WhatsApp' :
                                 module === 'order_api_keys' ? '🔑 API Keys' :
                                 module === 'social_count' ? '\uD83D\uDD0E Check Count API' :
                                 module === 'settings' ? '⚙️ Cài đặt' : module}
                              </h5>
                              <div style={{ 
                                display: 'flex', 
                                flexWrap: 'wrap', 
                                gap: '8px' 
                              }}>
                                {permissions.map((permission) => {
                                  const isAdminEditing = editingUser?.role === 'admin';
                                  const userPerm = userPermissions.find(p => 
                                    p.module === module && 
                                    p.permission === permission
                                  );
                                  const isRolePermission = rolePermissions.some(p => 
                                    p.module === module && 
                                    p.permission === permission
                                  );
                                  
                                  // Determine if permission is granted
                                  const isGranted = isAdminEditing ? true : (userPerm ? userPerm.granted : isRolePermission);
                                  
                                  return (
                                    <label
                                      key={permission}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        cursor: 'pointer',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        backgroundColor: isGranted ? '#dbeafe' : '#f3f4f6',
                                        border: '1px solid #d1d5db',
                                        fontSize: '13px'
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isGranted}
                                        onChange={editingUser?.role === 'admin' ? undefined : () => togglePermission(module, permission)}
                                        disabled={editingUser?.role === 'admin'}
                                        style={{ margin: 0 }}
                                      />
                                      <span style={{
                                        color: '#374151',
                                        fontWeight: 'normal'
                                      }}>
                                        {permission === 'read' ? 'Xem' :
                                         permission === 'create' ? 'Tạo' :
                                         permission === 'update' ? 'Sửa' :
                                         permission === 'delete' ? 'Xóa' :
                                         permission === 'cancel' ? 'Hủy đơn' :
                                         permission === 'bulk_update' ? 'Cập nhật hàng loạt' :
                                         permission === 'bulk_cancel' ? 'Hủy hàng loạt' :
                                         permission === 'refill' ? 'Bảo hành' :
                                         permission === 'bulk_refill' ? 'Bảo hành hàng loạt' :
                                         permission === 'check_status' ? 'Check Status Provider' :
                                         permission === 'clear_id_api' ? 'Xóa ID API' :
                                         permission === 'approve' ? 'Duyệt' :
                                         permission === 'write' ? 'Ghi' :
                                         permission === 'view_id_api' ? 'Xem ID API' :
                                         permission === 'view_provider' ? 'Xem Nhà cung cấp' :
                                         permission === 'view_profit' ? 'Xem Lợi nhuận' :
                                         permission === 'view_price_api' ? 'Xem Giá API' :
                                         permission === 'manage_permissions' ? 'Phân quyền' : permission}
                                      </span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>

                        <div style={{
                          marginTop: '16px',
                          textAlign: 'right'
                        }}>
                          {editingUser?.role !== 'admin' ? (
                            <button
                              type="button"
                              onClick={savePermissions}
                              style={{
                                padding: '8px 16px',
                                backgroundColor: '#059669',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '14px',
                                fontWeight: '500',
                                cursor: 'pointer'
                              }}
                            >
                              💾 Lưu quyền
                            </button>
                          ) : (
                            <span style={{ fontSize: '13px', color: '#6b7280' }}>
                              Admin không cần lưu quyền
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Toggle: change password placed right under permissions */}
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}>
                    <input
                      type="checkbox"
                      checked={changePassword}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setChangePassword(checked);
                        if (!checked) {
                          setFormData({ ...formData, password: '' });
                          setConfirmPassword('');
                          setFormErrors({ ...formErrors, password: '' });
                        }
                      }}
                      style={{ margin: 0 }}
                    />
                    🔒 Đổi mật khẩu
                  </label>
                </div>

                {changePassword && (
                  <>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                        Mật khẩu mới
                      </label>
                      <input
                        type="password"
                        name="newPassword"
                        autoComplete="new-password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="Ít nhất 8 ký tự, có chữ hoa, chữ thường, số và ký tự đặc biệt"
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: formErrors.password ? '2px solid #ef4444' : '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '14px',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                        Nhập lại mật khẩu mới
                      </label>
                      <input
                        type="password"
                        name="confirmNewPassword"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Nhập lại mật khẩu để xác nhận"
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: formErrors.password ? '2px solid #ef4444' : '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '14px',
                          boxSizing: 'border-box'
                        }}
                      />
                      {formErrors.password && (
                        <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                          {formErrors.password}
                        </div>
                      )}
                    </div>
                  </>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#f3f4f6',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    Hủy
                  </button>
                  <button
                    type="submit"
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#2563eb',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    {editingUser ? 'Cập nhật' : 'Thêm mới'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Users;